import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "@/components/motion";

const LINKS = [
  { label: "Home", path: "/" },
  { label: "Our Services", path: "/services" },
  { label: "About Us", path: "/about" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          open ? "bg-transparent" : scrolled ? "backdrop-blur-md bg-canvas/90 border-b border-subtle/80" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4 sm:py-5">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2">
            <span className={`font-serif text-2xl sm:text-[1.7rem] font-semibold tracking-[0.12em] transition-colors duration-300 ${open ? "text-canvas" : "text-blue"}`}>
              HOMELARA
            </span>
            <span className={`hidden sm:block font-mono text-[10px] uppercase tracking-[0.3em] ${open ? "text-sage" : "text-sage-dark"}`}>
              London
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
            {LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 hover:text-blue ${
                    isActive ? "text-blue border-b border-blue pb-1" : "text-faint"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/book"
              data-testid="nav-book-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-blue px-6 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-canvas transition-all duration-300 hover:bg-blue-hover hover:-translate-y-0.5"
            >
              Book a Clean
              <ArrowUpRight size={14} />
            </Link>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              className={`lg:hidden flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-300 ${
                open ? "border-canvas/30 bg-transparent text-canvas" : "border-subtle bg-surface text-blue"
              }`}
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-blue-midnight flex flex-col justify-center px-8"
          >
            <nav className="space-y-2">
              {LINKS.map((l, i) => (
                <div key={l.path} className="overflow-hidden">
                  <motion.div
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.6, delay: 0.06 * i, ease: EASE }}
                  >
                    <Link
                      to={l.path}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="block font-serif text-4xl text-canvas py-2 hover:text-sage transition-colors"
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                </div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="mt-10 space-y-4"
            >
              <Link
                to="/book"
                data-testid="mobile-nav-book-button"
                className="inline-flex items-center gap-2 rounded-full bg-canvas px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] text-blue"
              >
                Book a Clean <ArrowUpRight size={14} />
              </Link>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-sage">
                London Zones 1 & 2
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
